import { useEffect, useState } from 'react';
import { Button, Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCircle,
  faCircleCheck,
  faFileCsv,
  faFileZipper,
  faLock,
  faPaperPlane,
  faPrint,
} from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  formatDateDDMMYYYY,
  PNL_UNIT_NONE,
  type ClosedRunSummary,
  type PayrollReportRow,
  type PeriodSelection,
} from '@salaries/shared';
import {
  downloadClosedRunInvoices,
  getClosedRunReport,
  listClosedRuns,
  awaitRunJob,
  sendRunReport,
} from '../../api/payrollRun';
import { confirmModal } from '../../components/confirmModal';
import { Money } from '../../components/Money';
import { Loader, loaderIndicator } from '../../components/Loader';
import { useTableScrollY } from '../../components/useTableScrollY';
import { EmptyState } from '../../components/EmptyState';
import { roleLabels } from './roles';
import { exportPayrollCsv, exportPayrollPrint } from './payrollExport';
import { useAuth } from '../../auth/AuthContext';
import { enabledMechanisms, type BoxMechanism } from '@salaries/shared';
import { ReceiptPreview } from './ReceiptPreview';

// Identifies a frozen report — enough to open it without the full summary row
// (used when deep-linking straight from a just-closed period). One entry per
// (period, P&L unit) — a box that doesn't split by unit always has pnl_unit_id
// PNL_UNIT_NONE (0).
type OpenRun = Pick<
  ClosedRunSummary,
  'period_start' | 'period_end' | 'period_label' | 'pnl_unit_id' | 'pnl_unit_name'
>;

// Archive of closed (locked) payroll runs. The list opens a read-only frozen
// report rendered entirely from the snapshot — no live Arbox, so it always
// matches exactly what was finalized for that period.
export function ClosedRunsTab({
  initialOpen,
  onConsumed,
}: {
  // When set, open straight into this run's frozen report on mount (e.g. right
  // after closing a period in the report tab).
  initialOpen?: OpenRun | null;
  onConsumed?: () => void;
} = {}) {
  const auth = useAuth();
  // Same column set as the live report — see PayrollReportTab's boxMechanisms.
  const boxMechanisms = auth.settings ? enabledMechanisms(auth.settings) : (['group_classes'] as BoxMechanism[]);
  const [open, setOpen] = useState<OpenRun | null>(initialOpen ?? null);
  // Show the post-close success popup only when we arrived straight from closing
  // a period (a deep-link), not when a report is opened manually from the list.
  const [showCloseSuccess, setShowCloseSuccess] = useState(!!initialOpen);

  // Clear the parent's pending deep-link so re-visiting this tab later doesn't
  // force the report open again. Runs once on mount.
  useEffect(() => {
    if (initialOpen) onConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listQuery = useQuery({ queryKey: ['closed-runs'], queryFn: listClosedRuns });

  // Header stays fixed while rows scroll. Report: header + pagination chrome
  // (~96px), the תכלת summary bar (~44px), gutter. List: header only, no
  // pagination.
  const [reportWrapperRef, reportScrollY] = useTableScrollY(96 + 44 + 16);
  const [listWrapperRef, listScrollY] = useTableScrollY(40 + 16);

  const reportQuery = useQuery({
    queryKey: ['closed-run-report', open?.period_start, open?.period_end, open?.pnl_unit_id],
    queryFn: () => getClosedRunReport(open!.period_start, open!.period_end, open!.pnl_unit_id),
    enabled: open != null,
  });

  // Excel + PDF + an invoice ZIP + SES — slow enough that nobody should wait on
  // it, and nobody minds if it lands a few minutes later. Queued server-side;
  // the mutation settles as soon as it's accepted. Scoped to this row's own
  // P&L unit — sending סניף תל אביב's report never bundles in הרצליה's coaches.
  const sendReportMut = useMutation({
    mutationFn: () => sendRunReport(open!.period_start, open!.period_end, open!.pnl_unit_id),
    onSuccess: (r) => {
      toast(`מכינים את הדוח ל${r.to} — זה רץ ברקע, אפשר להמשיך לעבוד.`, {
        icon: '⏳',
        duration: 6000,
      });
      // Watch it to the end so "nothing to send" / "no recipient" surfaces
      // instead of the operator assuming it went out.
      const sel: PeriodSelection = {
        kind: 'custom',
        start_date: open!.period_start,
        end_date: open!.period_end,
      };
      void awaitRunJob(sel, 'accountant_report', { unitId: open!.pnl_unit_id })
        .then(() => toast.success(`הדוח נשלח ל${r.to}`))
        .catch((e: unknown) => toast.error((e as Error).message || 'שגיאה בשליחת הדוח'));
    },
    onError: (e) => toast.error((e as Error).message || 'שגיאה בשליחת הדוח'),
  });
  const confirmSendReport = () =>
    confirmModal({
      title: 'שליחת דוח שכר',
      content: open?.pnl_unit_name
        ? `לשלוח את דוח השכר של ${open.pnl_unit_name} (כולל חשבוניות) לכתובת המייל שהוגדרה?`
        : 'לשלוח את דוח השכר המרוכז (כולל חשבוניות) לכתובת המייל שהוגדרה?',
      okText: 'שלח',
      cancelText: 'ביטול',
      onOk: () => sendReportMut.mutateAsync(),
    });

  // Download the ZIP of all uploaded invoices/receipts for the open run.
  const invoicesMut = useMutation({
    mutationFn: () => downloadClosedRunInvoices(open!.period_start, open!.period_end, open!.pnl_unit_id),
    onSuccess: () => toast.success('החשבוניות הורדו'),
    onError: (e) => toast.error((e as Error).message || 'שגיאה בהורדת החשבוניות'),
  });

  if (open) {
    const rep = reportQuery.data;
    const rows = rep?.rows ?? [];
    // A closed-run report is always scoped to ONE P&L unit (the row that was
    // opened), so every row here shares it — no per-row unit column needed.
    const selection: PeriodSelection = { kind: 'custom', start_date: open.period_start, end_date: open.period_end };
    const reportColumns: ColumnsType<PayrollReportRow> = [
      { title: 'שם', dataIndex: 'display_name', fixed: 'right', width: 150 },
      {
        title: 'תפקידים',
        dataIndex: 'user_role',
        width: 160,
        render: (v: string | null) => {
          const labels = roleLabels(v);
          return labels.length ? labels.join(' · ') : '—';
        },
      },
      { title: 'שכר שיעורים', width: 110, dataIndex: 'class_pay', align: 'right', render: (v) => <Money value={v} /> },
      { title: 'תוספת סופ"ש', width: 110, dataIndex: 'weekend_bonus', align: 'right', render: (v) => <Money value={v} /> },
      { title: 'תוספת שיעור', width: 110, dataIndex: 'class_bonus', align: 'right', render: (v) => <Money value={v} /> },
      { title: 'בונוס המרה', width: 110, dataIndex: 'conversion_bonus', align: 'right', render: (v) => <Money value={v} /> },
      { title: 'שכר בסיס', width: 110, dataIndex: 'base_salary', align: 'right', render: (v) => <Money value={v} /> },
      { title: 'נסיעות', width: 100, dataIndex: 'travel', align: 'right', render: (v) => <Money value={v} /> },
      // מוצרים נלווים (hugim) — only on a run where someone was paid on them.
      // Already inside the total; this just shows how much of it they were.
      ...(rows.some((r) => (r.addons_pay ?? 0) !== 0)
        ? ([
            {
              title: 'תשלום על נלווים',
              width: 120,
              dataIndex: 'addons_pay',
              align: 'right',
              render: (v: number | undefined) =>
                v ? <Money value={v} /> : <span className="text-secondary-text">—</span>,
            },
          ] as ColumnsType<PayrollReportRow>)
        : []),
      // תוספות וקיזוזים ידניים (hugim) — only on a run where someone has one.
      ...(rows.some((r) => (r.manual_additions ?? 0) !== 0 || (r.manual_offsets ?? 0) !== 0)
        ? ([
            {
              title: 'תוספות',
              width: 100,
              dataIndex: 'manual_additions',
              align: 'right',
              render: (v: number | undefined) => <Money value={v ?? 0} />,
            },
            {
              title: 'קיזוזים',
              width: 100,
              dataIndex: 'manual_offsets',
              align: 'right',
              render: (v: number | undefined) =>
                v ? (
                  <span className="text-error-red">
                    -<Money value={v} />
                  </span>
                ) : (
                  <Money value={0} />
                ),
            },
          ] as ColumnsType<PayrollReportRow>)
        : []),
      { title: 'בונוס חד-פעמי', width: 120, dataIndex: 'one_time_bonus', align: 'right', render: (v) => <Money value={v} /> },
      // Stored positive, shown negative — it came OFF this line's total.
      {
        title: 'הפחתה',
        dataIndex: 'deduction',
        width: 100,
        align: 'right',
        render: (v: number | undefined) => (v ? <Money value={-v} /> : <span className="text-secondary-text">—</span>),
      },
      {
        title: 'חשבונית',
        dataIndex: 'receipt_uploaded',
        width: 70,
        align: 'center',
        render: (uploaded: boolean, row) =>
          uploaded ? (
            <ReceiptPreview
              staffId={row.arbox_users_boxes_id}
              unitId={row.pnl_unit_id}
              selection={selection}
              invoiceUrl={row.invoice_url}
              checkStatus={row.invoice_check_status}
              checkIssues={row.invoice_check_issues}
            />
          ) : (
            <span className="text-secondary-text">—</span>
          ),
      },
      {
        title: 'סה"כ',
        dataIndex: 'total',
        fixed: 'right',
        width: 110,
        align: 'right',
        render: (v) => (
          <span className="font-semibold">
            <Money value={v} />
          </span>
        ),
      },
    ];

    // Numeric total width → table-layout:fixed, so the fixed header and the
    // scrolling body line up (see useTableScrollY).
    const reportScrollX = reportColumns.reduce(
      (sum, c) => sum + (typeof c.width === 'number' ? c.width : 100),
      0,
    );

    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex items-center gap-3 mb-3">
          <Button icon={<FontAwesomeIcon icon={faArrowRight} />} onClick={() => setOpen(null)}>
            חזרה לרשימה
          </Button>
          <span className="text-main-text font-medium">
            {open.period_label}
            {open.pnl_unit_name && ` · ${open.pnl_unit_name}`}
          </span>
          <span className="text-small text-success-green">
            <FontAwesomeIcon icon={faLock} style={{ marginInlineEnd: 6 }} />
            דוח סגור
          </span>
          <div className="flex-1" />
          <Button
            icon={<FontAwesomeIcon icon={faFileCsv} />}
            disabled={rows.length === 0}
            onClick={() => exportPayrollCsv(rows, open.period_label, boxMechanisms)}
          >
            ייצוא ל-CSV
          </Button>
          <Button
            icon={<FontAwesomeIcon icon={faPrint} />}
            disabled={rows.length === 0}
            onClick={() => exportPayrollPrint(rows, open.period_label, boxMechanisms)}
          >
            הדפסה / PDF
          </Button>
          {/* The invoices ZIP used to be reachable ONLY from the post-close
              popup, which shows once and never again — so a box that opened the
              run from this archive later (or dismissed the popup) had no way to
              download the bundle at all, only to re-send it to the accountant
              (box 483, 2026-09-09). Same endpoint, scoped to this run's unit. */}
          <Button
            icon={<FontAwesomeIcon icon={faFileZipper} />}
            // Nothing to bundle when no coach in this run provided an invoice —
            // the endpoint would only 404 into a toast.
            disabled={!rows.some((r) => r.receipt_uploaded)}
            loading={invoicesMut.isPending}
            onClick={() => invoicesMut.mutate()}
          >
            הורדת חשבוניות
          </Button>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faPaperPlane} />}
            disabled={rows.length === 0}
            loading={sendReportMut.isPending}
            onClick={confirmSendReport}
          >
            שלח לרו"ח
          </Button>
        </div>

        {reportQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader size={48} />
          </div>
        ) : (
          <div ref={reportWrapperRef} className="flex-1 min-h-0 flex flex-col">
            <Table<PayrollReportRow>
              rowKey={(r) => `${r.arbox_users_boxes_id}:${r.pnl_unit_id ?? 0}`}
              size="small"
              columns={reportColumns}
              dataSource={rows}
              scroll={{ x: reportScrollX, y: reportScrollY }}
              pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
            />
          </div>
        )}

        {/* תכלת summary bar — same strip as the live report; frozen snapshot, so
            it totals the whole run (no filtering on a closed report). */}
        {!reportQuery.isLoading && rows.length > 0 && (
          <div style={{ background: '#DFF1FF' }} className="shrink-0">
            <div className="px-2 py-2 flex items-center justify-center flex-wrap gap-x-6 gap-y-1 text-small text-main-text">
              <span>
                כמות אנשי צוות <span className="font-semibold">{rows.length}</span>
              </span>
              <FontAwesomeIcon icon={faCircle} style={{ fontSize: 3, color: '#3b3b3b' }} />
              <span className="flex items-center gap-1">
                מע&quot;מ <span className="font-semibold"><Money value={rows.reduce((a, r) => a + r.vat, 0)} /></span>
              </span>
              <FontAwesomeIcon icon={faCircle} style={{ fontSize: 3, color: '#3b3b3b' }} />
              <span className="flex items-center gap-1">
                סה&quot;כ <span className="font-semibold"><Money value={rows.reduce((a, r) => a + r.total, 0)} /></span>
              </span>
            </div>
          </div>
        )}

        {/* Post-close success popup — a quick action hub for the run we just
            froze: send to the accountant, download the report, or download the
            invoices ZIP. Shown only when arriving straight from a close. */}
        <Modal
          open={showCloseSuccess}
          onCancel={() => setShowCloseSuccess(false)}
          footer={null}
          centered
          width={400}
          closeIcon={<FontAwesomeIcon icon={faXmark} style={{ fontSize: 18, color: '#9D9D9D' }} />}
        >
          <div className="text-center pt-2 pb-1">
            <FontAwesomeIcon icon={faCircleCheck} className="text-success-green" style={{ fontSize: 48 }} />
            <div className="text-heading-2 text-main-text mt-3">התקופה נסגרה</div>
            <div className="text-small text-secondary-text mb-5">
              {open.period_label}
              {open.pnl_unit_name && ` · ${open.pnl_unit_name}`} · נוצר דוח קפוא
            </div>
            <div className="flex flex-col gap-2">
              <Button
                type="primary"
                size="large"
                block
                icon={<FontAwesomeIcon icon={faPaperPlane} />}
                loading={sendReportMut.isPending}
                onClick={() => sendReportMut.mutate()}
              >
                שליחה לרו&quot;ח
              </Button>
              <Button
                size="large"
                block
                icon={<FontAwesomeIcon icon={faFileCsv} />}
                disabled={reportQuery.isLoading || rows.length === 0}
                onClick={() => exportPayrollCsv(rows, open.period_label, boxMechanisms)}
              >
                הורדה למחשב (CSV)
              </Button>
              <Button
                size="large"
                block
                icon={<FontAwesomeIcon icon={faFileZipper} />}
                loading={invoicesMut.isPending}
                onClick={() => invoicesMut.mutate()}
              >
                הורדת החשבוניות / קבלות
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  const runs = listQuery.data ?? [];
  // One archive row per (period, P&L unit) for a box that splits — show which
  // branch each closed row belongs to.
  const anyUnits = runs.some((r) => r.pnl_unit_id !== PNL_UNIT_NONE);
  const listColumns: ColumnsType<ClosedRunSummary> = [
    { title: 'תקופה', dataIndex: 'period_label' },
    ...(anyUnits
      ? ([
          {
            title: 'יחידת רווח והפסד',
            dataIndex: 'pnl_unit_name',
            render: (v: string | null) => v ?? '—',
          },
        ] as ColumnsType<ClosedRunSummary>)
      : []),
    {
      title: 'נסגר בתאריך',
      dataIndex: 'locked_at',
      render: (v: string | null) => (v ? formatDateDDMMYYYY(v) : '—'),
    },
    { title: 'אנשי צוות', dataIndex: 'staff_count', align: 'right' },
    { title: 'סה"כ', dataIndex: 'total', align: 'right', render: (v) => <Money value={v} /> },
    {
      title: '',
      key: 'open',
      fixed: 'right',
      width: 100,
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => setOpen(r)}>
          פתח דוח
        </Button>
      ),
    },
  ];

  if (listQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader size={48} />
      </div>
    );
  }

  return (
    <div ref={listWrapperRef} className="flex-1 min-h-0 flex flex-col">
      <Table<ClosedRunSummary>
        rowKey={(r) => `${r.period_start}_${r.period_end}_${r.pnl_unit_id}`}
        size="small"
        columns={listColumns}
        dataSource={runs}
        loading={listQuery.isFetching ? { spinning: true, indicator: loaderIndicator('sm') } : false}
        pagination={false}
        scroll={{ y: listScrollY }}
        locale={{
          emptyText: (
            <EmptyState
              icon={<FontAwesomeIcon icon={faLock} style={{ fontSize: 42 }} />}
              title="אין דוחות סגורים"
              description='סגור תקופה בלשונית "דוח שכר" כדי ליצור דוח סופי קפוא שיופיע כאן.'
            />
          ),
        }}
      />
    </div>
  );
}
